import { PaginationModel } from '@/core/types/models/pagination.model'
import axios from 'axios'
import { useCallback, useEffect, useRef } from 'react'
import { createInfiniteFetchStore } from './store'

type FetchResponse<T extends unknown[], K extends string> = {
  [P in K]: T
} & {
  pagination: PaginationModel
}

type UseInfiniteFetchProps<K extends string> = {
  initialPage?: number
  limit?: number
  fetchDelay?: number
  dataKey: K
  enabled?: boolean
  fetchParams?: object
}

export function useInfiniteFetch<T extends unknown[], K extends string>(
  fetchFunction: (
    params: {
      page: number
      limit: number
    },
    config?: { controller?: AbortController },
  ) => Promise<FetchResponse<T, K>>,
  {
    initialPage = 1,
    limit = 10,
    dataKey = 'data' as K,
    enabled = true,
    fetchParams,
  }: UseInfiniteFetchProps<K> = { dataKey: 'data' as K },
) {
  const storeRef = useRef<ReturnType<
    typeof createInfiniteFetchStore<T>
  > | null>(null)

  if (storeRef.current === null) {
    storeRef.current = createInfiniteFetchStore<T>(initialPage)
  }
  const useStore = storeRef.current

  const {
    items,
    hasMore,
    isLoading,
    page,
    error,
    pagination,
    appendItems,
    setHasMore,
    setIsLoading,
    setPage,
    setError,
    setPagination,
    resetState,
  } = useStore()

  const loadItems = useCallback(
    async (currentPage: number, controller: AbortController) => {
      try {
        setIsLoading(true)
        setError(null)

        const response = await fetchFunction(
          {
            page: currentPage,
            limit,
            ...fetchParams,
          },
          { controller },
        )

        const data = response[dataKey]

        setHasMore(!response.pagination.isLast)
        setPagination(response.pagination)
        appendItems(data)
      } catch (err) {
        if (!axios.isCancel(err)) {
          setError(err as Error)
        }
      } finally {
        setIsLoading(false)
      }
    },
    [
      setIsLoading,
      setError,
      fetchFunction,
      limit,
      setHasMore,
      setPagination,
      appendItems,
      dataKey,
      fetchParams,
    ],
  )

  useEffect(() => {
    if (!enabled) return
    const controller = new AbortController()

    loadItems(page, controller)

    return () => {
      controller.abort()
    }
  }, [loadItems, page, enabled])

  const onLoadMore = useCallback(() => {
    if (enabled && hasMore && !isLoading) {
      setPage(page + 1)
    }
  }, [enabled, hasMore, isLoading, page, setPage])

  return {
    items,
    hasMore,
    isLoading,
    error,
    pagination,
    onLoadMore,
    reset: resetState,
  }
}
